/**
 * 맥스매치 Lite(mini) 엑셀 행 매핑.
 * Full excel-details 응답도 동일 컬럼으로 맞춰 다운로드한다.
 *
 * Full(V121+) 스냅샷: priceOrig, totalAmount, standardCommissionRateOrig/New.
 * 수수료 금액만 이름 규약이 다름 — originEstimatedAmount / newEstimatedAmount.
 * 스냅샷 값이 0 이어도 0 으로 표기한다.
 */
export type MaxMatchLiteExcelRow = {
  email: string | null;
  clinicName: string | null;
  prescriptionYearMonth: string | null;
  pharmaCompanyNameOrig: string | null;
  drugCodeOrig: string | null;
  drugNameOrig: string | null;
  outHospitalQuantity: unknown;
  priceOrig: unknown;
  totalAmount: unknown;
  standardCommissionRateOrig: unknown;
  commissionAmountOrig: unknown;
  pharmaCompanyNameNew: string | null;
  drugCodeNew: string | null;
  drugNameNew: string | null;
  priceNew: unknown;
  /** 처방량 × M_약가 (원본 처방 금액과 동일하게 floor) */
  totalAmountNew: number | null;
  standardCommissionRateNew: unknown;
  commissionAmountNew: unknown;
  status: string | null;
  createdDate: string | null;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const pickRaw = (row: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key];
  }
  return null;
};

const pickString = (
  row: Record<string, unknown>,
  ...keys: string[]
): string | null => {
  const value = pickRaw(row, ...keys);
  if (value == null) return null;
  return String(value);
};

const toFiniteNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * M_처방 금액 = floor(처방량 × M_약가).
 * 원본 처방 금액(totalAmount)과 동일하게 곱한 뒤 내림.
 */
export const computeTotalAmountNew = (
  outHospitalQuantity: unknown,
  priceNew: unknown,
): number | null => {
  const qty = toFiniteNumber(outHospitalQuantity);
  const price = toFiniteNumber(priceNew);
  if (qty == null || price == null) return null;
  return Math.floor(qty * price);
};

/**
 * Lite·Full excel-details JSON 한 행을 Lite 엑셀 컬럼으로 정규화한다.
 *
 * Full 필드 대응:
 * - priceOrig / totalAmount / standardCommissionRate* → 동명 (V121+)
 * - originEstimatedAmount → commissionAmountOrig
 * - newEstimatedAmount → commissionAmountNew
 * - createdAt → createdDate
 */
export const mapMaxMatchLiteExcelRow = (
  raw: Record<string, unknown>,
): MaxMatchLiteExcelRow => {
  const row = asRecord(raw);
  const outHospitalQuantity = pickRaw(
    row,
    'outHospitalQuantity',
    'out_hospital_quantity',
  );
  const priceNew = pickRaw(row, 'priceNew', 'price_new');

  return {
    email: pickString(row, 'email'),
    clinicName: pickString(row, 'clinicName', 'clinic_name'),
    prescriptionYearMonth: pickString(
      row,
      'prescriptionYearMonth',
      'prescription_year_month',
      'refYm',
      'ref_ym',
    ),
    pharmaCompanyNameOrig: pickString(
      row,
      'pharmaCompanyNameOrig',
      'pharma_company_name_orig',
    ),
    drugCodeOrig: pickString(row, 'drugCodeOrig', 'drug_code_orig'),
    drugNameOrig: pickString(row, 'drugNameOrig', 'drug_name_orig'),
    // 원외 수량은 절삭하지 않은 원값 그대로 (미니·풀 공통)
    outHospitalQuantity,
    priceOrig: pickRaw(row, 'priceOrig', 'price_orig'),
    totalAmount: pickRaw(row, 'totalAmount', 'total_amount'),
    standardCommissionRateOrig: pickRaw(
      row,
      'standardCommissionRateOrig',
      'standard_commission_rate_orig',
    ),
    commissionAmountOrig: pickRaw(
      row,
      'commissionAmountOrig',
      'commission_amount_orig',
      'originEstimatedAmount',
      'origEstimatedAmount',
      'orig_estimated_amount',
    ),
    pharmaCompanyNameNew: pickString(
      row,
      'pharmaCompanyNameNew',
      'pharma_company_name_new',
    ),
    drugCodeNew: pickString(row, 'drugCodeNew', 'drug_code_new'),
    drugNameNew: pickString(row, 'drugNameNew', 'drug_name_new'),
    priceNew,
    totalAmountNew: computeTotalAmountNew(outHospitalQuantity, priceNew),
    standardCommissionRateNew: pickRaw(
      row,
      'standardCommissionRateNew',
      'standard_commission_rate_new',
    ),
    commissionAmountNew: pickRaw(
      row,
      'commissionAmountNew',
      'commission_amount_new',
      'newEstimatedAmount',
      'new_estimated_amount',
    ),
    status: pickString(row, 'status', 'maxMatchApprovalStatus'),
    createdDate: pickString(
      row,
      'createdDate',
      'created_date',
      'createdAt',
      'created_at',
    ),
  };
};

export const mapMaxMatchLiteExcelRows = (
  rows: Record<string, unknown>[],
): MaxMatchLiteExcelRow[] => rows.map(mapMaxMatchLiteExcelRow);
